import { cn } from '@/lib/ui/style-helpers'

import { ITEM_ICON_BY_STATE } from '@/pages/company-drawer/company-overview/set-irq-modal/irq-sidebar/consts'
import {
  itemIconClasses,
  itemRowClasses,
  itemTextClasses,
} from '@/pages/company-drawer/company-overview/set-irq-modal/irq-sidebar/variants'
import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'
import {
  STEP_KIND,
  type SidebarItemState,
  type SidebarRenderItem,
} from '@/pages/company-drawer/company-overview/set-irq-modal/types'

import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

type SidebarItemProps = {
  item: SidebarRenderItem
  currentIndex: number
  validatedSet: Set<string>
  onStepClick: (index: number) => void
}

export const SidebarItem = ({
  item,
  currentIndex,
  validatedSet,
  onStepClick,
}: SidebarItemProps) => {
  if (item.kind === 'separator') {
    return <div className='my-2 border-t border-main-border-default' role='presentation' />
  }

  if (item.kind === 'category-header') {
    return (
      <div className='mt-4 px-2 pb-1 text-main-foreground-tertiary text-label-small'>
        {item.category}
      </div>
    )
  }

  const isActive = item.index === currentIndex
  const isVisited =
    item.kind === STEP_KIND.QUESTION
      ? validatedSet.has(item.group.id) && !isActive
      : item.index < currentIndex

  // A conditional question whose condition isn't met renders disabled and isn't navigable.
  const state: SidebarItemState =
    item.kind === STEP_KIND.QUESTION && !item.isActive
      ? 'conditional'
      : isActive
        ? 'active'
        : isVisited
          ? 'visited'
          : 'idle'

  const label =
    item.kind === STEP_KIND.OVERVIEW
      ? SET_IRQ_MODAL_LABEL.OVERVIEW
      : item.kind === STEP_KIND.ASSIGN
        ? SET_IRQ_MODAL_LABEL.ASSIGN_INHERENT_RISK
        : item.group.question

  const Icon = ITEM_ICON_BY_STATE[state]

  const content = (
    <>
      <span
        className={cn(
          'inline-flex size-4 shrink-0 items-center justify-center',
          itemIconClasses[state],
        )}
      >
        <Icon size={16} strokeWidth={1.5} />
      </span>
      <span className={cn('truncate', itemTextClasses[state])}>{label}</span>
    </>
  )

  const rowClasses = cn(
    'group flex h-8 w-full items-center gap-2 rounded px-2 text-left transition-colors text-body-small',
    itemRowClasses[state],
  )

  const row =
    state === 'conditional' ? (
      <div className={rowClasses} aria-disabled>
        {content}
      </div>
    ) : (
      <button
        type='button'
        onClick={() => onStepClick(item.index)}
        className={cn(
          rowClasses,
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main-border-focus',
        )}
      >
        {content}
      </button>
    )

  if (item.kind !== STEP_KIND.QUESTION) return row

  return (
    <Tooltip triggerClassName='w-full' asChild side='right' size='large' trigger={row}>
      {label}
    </Tooltip>
  )
}
